/**
 * ROD Review Dialog for Wholescale action.
 * Uses shared BaseModal and review components for consistency with campaigns.
 * Includes Wholescale blackbox integration.
 */
import { useState, useEffect } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as yup from 'yup';

import { api, useAPIContext } from '../../api/context';
import { useFPContext } from '../../utils/fingerprint';
import wholescale from '../../utils/wholescale';
import toast from '../../utils/toast';
import review from '../../components/review';
import BaseModal from '../BaseModal';
import styles from './RODReviewWholescale.module.scss';

const RODReviewWholescale = ({ item, onClose, onSubmit }) => {
  const { token } = useAPIContext();
  const { fp } = useFPContext();

  const config = item.config || {};
  const product = item.product;
  const previousData = item.previousData || {};
  const prefix = config.prefix || '';

  const [submitLoading, setSubmitLoading] = useState(false);
  const [blackboxReady, setBlackboxReady] = useState(false);

  // Install Wholescale snare.js on mount and poll for readiness
  useEffect(() => {
    wholescale.install();
    
    // Poll for blackbox readiness
    const checkReady = () => {
      const bb = wholescale.blackbox();
      if (bb !== undefined) {
        setBlackboxReady(true);
      }
    };
    
    checkReady();
    const interval = setInterval(checkReady, 500);
    
    // Give it 10 seconds max
    const timeout = setTimeout(() => {
      clearInterval(interval);
      if (!blackboxReady) {
        setBlackboxReady(true); // Allow submission anyway, will fail gracefully
      }
    }, 10000);
    
    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [blackboxReady]);

  const submit = async (values) => {
    setSubmitLoading(true);

    try {
      const fpResult = await fp.get();
      const blackbox = wholescale.blackbox();

      // Validate blackbox is available
      if (!blackbox) {
        setSubmitLoading(false);
        toast.show(toast.KIND.ERROR, 'Security verification not ready. Please wait a moment and try again.');
        return;
      }

      const response = await api(token).v1.products.reviews_on_demand.submit.post(
        { rod_product_id: item.rod_id },
        {
          data_json: JSON.stringify({
            ...values,
            blackbox,
          }),
          fingerprint: fpResult.visitorId,
        }
      );

      setSubmitLoading(false);

      if (response.status === 201) {
        if (onSubmit) onSubmit(response.body);
        if (onClose) onClose();
      } else if (response.status === 400) {
        toast.show(toast.KIND.ERROR, response.body?.error || response.body);
      }
    } catch (err) {
      setSubmitLoading(false);
      toast.show(toast.KIND.WARNING, err.message || err);
    }
  };

  const loading = submitLoading || !blackboxReady;

  return (
    <BaseModal title="Wholescale" onClose={onClose}>
      <div className={styles.root}>
        {/* Product header */}
        <div className={styles.product}>
          {product.images?.[0] && (
            <img src={product.images[0].image} alt="" />
          )}
          <div>
            <span>{product.brand_name}</span>
            <strong>{product.name}</strong>
          </div>
        </div>

        <Formik
          initialValues={{
            stars: previousData.stars || 0,
            title: previousData.title || '',
            text: previousData.text || prefix,
          }}
          validationSchema={yup.object({
            stars: yup.number()
              .min(1, 'Rating is required')
              .max(5, 'Invalid rating'),
            title: yup.string()
              .required('Title is required'),
            text: yup.string()
              .required('Review text is required'),
          })}
          validateOnChange={false}
          onSubmit={(values) => {
            if (loading) return;
            submit(values);
          }}
        >
          <Form>
            <review.Stars
              className={styles.stars}
              loading={loading}
              count={5}
            />

            <div className={styles.field}>
              <label htmlFor='title'>Title</label>
              <Field
                id='title'
                aria-label='title'
                name='title'
                type='text'
                disabled={loading}
              />
              <ErrorMessage
                className={styles.error}
                name='title'
                component='div'
              />
            </div>

            <review.Text
              className={styles.text}
              loading={loading}
              label={(
                <>
                  Review
                  {config.prefix && (
                    <span>
                      (Disclosure text added - DO NOT REMOVE THIS TEXT)
                    </span>
                  )}
                </>
              )}
              prefix={prefix}
              length={{
                min: config.min_length || 0,
                max: config.max_length || 0,
              }}
              nlp={{
                include: config.nlp_include,
                exclude: config.nlp_exclude,
                exclude_default: config.nlp_exclude_default,
              }}
            />

            <button
              type="submit"
              disabled={loading}
            >
              {submitLoading ? 'Submitting...' : !blackboxReady ? 'Loading...' : 'Submit Review'}
            </button>
          </Form>
        </Formik>
      </div>
    </BaseModal>
  );
};

export default RODReviewWholescale;
